import { c as _c } from "react/compiler-runtime";
import { postEntitiesByFpIdVaultDecrypt } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { getLogger } from '@onefootprint/logger';
import type { DataIdentifier, Entity, PiiJsonValue } from '@onefootprint/request-types/dashboard';
import { useTranslation } from 'react-i18next';
import type useEntityVault from '@/entities/hooks/use-entity-vault';
import { useDecryptForm } from '@/entity/components/decrypt-form';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import useDocumentField from '@/entity/hooks/use-document-field';
import useDocumentText from '@/entity/hooks/use-document-text';
import type { TransformedDocument } from '@/entity/hooks/use-documents';
import useDocumentsQueryParams from '@/entity/hooks/use-documents-query-params';
import DocumentViewer, { type DocumentUpload } from '@/features/document-viewer';
const {
  logError
} = getLogger({
  location: 'document-viewer-container'
});
type DocumentViewerContainerProps = {
  document: TransformedDocument;
  entity: Entity;
  vault: ReturnType<typeof useEntityVault>;
  seqno?: number;
};
const DocumentViewerContainer = t0 => {
  const $ = _c(47);
  const {
    document,
    entity,
    vault,
    seqno
  } = t0;
  const {
    t
  } = useTranslation("entity-details");
  const form = useDecryptForm();
  const decryptControls = useDecryptControls();
  let t1;
  if ($[0] !== entity || $[1] !== seqno) {
    t1 = {
      entity,
      seqno
    };
    $[0] = entity;
    $[1] = seqno;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    getProps,
    getRelatedDisByFormValue
  } = useDocumentField(t1);
  let t2;
  if ($[3] !== document || $[4] !== getProps) {
    t2 = getProps(document);
    $[3] = document;
    $[4] = getProps;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    isDecrypted
  } = t2;
  const {
    clear
  } = useDocumentsQueryParams();
  const getDocText = useDocumentText();
  let t3;
  if ($[6] !== vault.data?.vault) {
    t3 = vault.data?.vault ?? {};
    $[6] = vault.data?.vault;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const vaultData = t3 as Record<string, string | undefined>;
  const showErrorToast = useRequestErrorToast();
  const isCustomDocument = document.kind === "custom";
  let t4;
  if ($[8] !== document.uploads || $[9] !== isCustomDocument || $[10] !== vaultData) {
    let t5;
    if ($[12] !== isCustomDocument || $[13] !== vaultData) {
      t5 = upload => ({
        side: isCustomDocument ? "document" : upload.side,
        timestamp: upload.timestamp,
        failureReasons: upload.failureReasons.map(String),
        isLatest: upload.isLatest,
        imageData: vaultData[`${upload.identifier}:${upload.version}`] ?? null
      });
      $[12] = isCustomDocument;
      $[13] = vaultData;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t4 = document.uploads.map(t5);
    $[8] = document.uploads;
    $[9] = isCustomDocument;
    $[10] = vaultData;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const uploads = t4;
  let t5;
  if ($[15] !== document.uploads[0] || $[16] !== document.uploads.length || $[17] !== isDecrypted || $[18] !== vaultData) {
    t5 = () => {
      if (!isDecrypted || document.uploads.length === 0) {
        return null;
      }
      const firstUpload = document.uploads[0];
      return vaultData[`${firstUpload.identifier}:${firstUpload.version}`] ?? null;
    };
    $[15] = document.uploads[0];
    $[16] = document.uploads.length;
    $[17] = isDecrypted;
    $[18] = vaultData;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const getDocumentSource = t5;
  let t6;
  if ($[20] !== decryptControls || $[21] !== document || $[22] !== entity || $[23] !== form || $[24] !== getRelatedDisByFormValue || $[25] !== t || $[26] !== vault) {
    t6 = async () => {
      const fields = getRelatedDisByFormValue(document);
      form.setFields(fields);
      const response = await decryptControls.decrypt({
        fields,
        entity,
        reason: t(_temp)
      });
      if (response) {
        vault.update({
          vault: response,
          transforms: {},
          dataKinds: {}
        });
      } else {
        logError("Vault decryption error", undefined, {
          entityId: entity.id,
          fields: fields.join(","),
          detail: "No response in DocumentViewerContainer"
        });
      }
    };
    $[20] = decryptControls;
    $[21] = document;
    $[22] = entity;
    $[23] = form;
    $[24] = getRelatedDisByFormValue;
    $[25] = t;
    $[26] = vault;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const handleDecrypt = t6;
  let t7;
  if ($[28] !== entity.id || $[29] !== showErrorToast || $[30] !== t) {
    t7 = async dataIdentifiers => {
      ;
      try {
        const response_0 = await postEntitiesByFpIdVaultDecrypt({
          path: {
            fpId: entity.id
          },
          body: {
            fields: dataIdentifiers,
            reason: t(_temp2)
          }
        });
        return response_0.data as Record<string, PiiJsonValue> | null;
      } catch (t8) {
        const error = t8;
        logError("Vault decryption error", error, {
          entityId: entity.id,
          fields: dataIdentifiers.join(",")
        });
        showErrorToast(error);
        return null;
      }
    };
    $[28] = entity.id;
    $[29] = showErrorToast;
    $[30] = t;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  const handleDecryptVaultData = t7;
  let t8;
  if ($[32] !== document.kind || $[33] !== document.uploads[0]?.identifier || $[34] !== getDocText) {
    t8 = document.kind === "custom" ? document.uploads[0]?.identifier : getDocText(document.kind);
    $[32] = document.kind;
    $[33] = document.uploads[0]?.identifier;
    $[34] = getDocText;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  const title = t8;
  const firstUpload_0 = document.uploads[0];
  const documentIdentifier = firstUpload_0 ? `${firstUpload_0.identifier}:${firstUpload_0.version}` : "";
  const t9 = entity.id;
  const t10 = title ?? "";
  let t11;
  if ($[36] !== getDocumentSource) {
    t11 = getDocumentSource();
    $[36] = getDocumentSource;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== clear || $[39] !== documentIdentifier || $[40] !== entity.id || $[41] !== handleDecrypt || $[42] !== handleDecryptVaultData || $[43] !== t10 || $[44] !== t11 || $[45] !== uploads) {
    t12 = <DocumentViewer fpId={t9} documentIdentifier={documentIdentifier} documentName={t10} documentSource={t11} onBack={clear} onDecrypt={handleDecrypt} onDecryptVaultData={handleDecryptVaultData} uploads={uploads} />;
    $[38] = clear;
    $[39] = documentIdentifier;
    $[40] = entity.id;
    $[41] = handleDecrypt;
    $[42] = handleDecryptVaultData;
    $[43] = t10;
    $[44] = t11;
    $[45] = uploads;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  return t12;
};
export default DocumentViewerContainer;
function _temp($) {
  return $.decrypt.documentDetailsReason;
}
function _temp2($_0) {
  return $_0.decrypt.documentDetailsReason;
}